import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

// ─── Auth Guard ────────────────────────────────────────────────────────────────
function RequireAuth({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const token = localStorage.getItem('access_token');
  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}
import MainLayout from './components/layout/MainLayout';
import DashboardNationalPage from './pages/DashboardNational';
import AssessmentListPage    from './pages/AssessmentList';
import AssessmentDetailPage  from './pages/AssessmentDetail';
import ChecklistPage         from './pages/ChecklistPage';
import RemediationPage       from './pages/RemediationPage';
import LoginPage             from './pages/LoginPage';

// User Portal Pages
import PortalHistoryPage       from './pages/PortalHistory';
import PortalReportingForm     from './pages/PortalReportingForm';

// Agent Node Pages
import AgentDashboard  from './pages/agent/AgentDashboard';
import KpiCollector    from './pages/agent/KpiCollector';
import KeyManagement   from './pages/agent/KeyManagement';
import Connections     from './pages/agent/Connections';
import DataSources     from './pages/agent/DataSources';
import DataAssets      from './pages/agent/DataAssets';
import Reports         from './pages/agent/Reports';
import Security        from './pages/agent/Security';

// Agent Pages
import KpiFormPage            from './pages/KpiFormPage';

// New Sub-modules Placholder Pages
import AutoAuditPage          from './pages/AutoAuditPage';
import SecurityAuditPage      from './pages/SecurityAuditPage';
import ReportsPage            from './pages/ReportsPage';
import GlobalRemediationsPage from './pages/GlobalRemediationsPage';
import AlertsPage             from './pages/AlertsPage';
import UsersPage              from './pages/UsersPage';
import SettingsPage           from './pages/SettingsPage';
import ChecklistsPage         from './pages/ChecklistsPage';
import PortalGuidancePage     from './pages/PortalGuidance';
import ForensicStorePage      from './pages/admin/ForensicStore';

export default function App() {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'dark' || saved === 'light') return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  const portalType = import.meta.env.VITE_PORTAL_TYPE || 'admin';
  const isAdmin = portalType === 'admin';
  const isAgent = portalType === 'agent';

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/*"
        element={
          <RequireAuth>
          <MainLayout theme={theme} onToggleTheme={toggleTheme} portalType={portalType as 'admin' | 'user' | 'agent'}>
            <Routes>
              {isAgent ? (
                <>
                  {/* Agent Node Routes */}
                  <Route path="/agent/dashboard"      element={<AgentDashboard />} />
                  <Route path="/agent/kpi-collector"  element={<KpiCollector />} />
                  <Route path="/agent/keys"           element={<KeyManagement />} />
                  <Route path="/agent/connections"    element={<Connections />} />
                  <Route path="/agent/data-sources"   element={<DataSources />} />
                  <Route path="/agent/data-assets"    element={<DataAssets />} />
                  <Route path="/agent/reports"        element={<Reports />} />
                  <Route path="/agent/security"       element={<Security />} />

                  <Route path="/"                   element={<Navigate to="/agent/dashboard" replace />} />
                  <Route path="/agent"              element={<Navigate to="/agent/dashboard" replace />} />
                </>
              ) : isAdmin ? (
                <>
                  {/* Admin Routes */}
                  <Route path="/dashboard"                      element={<DashboardNationalPage />} />
                  <Route path="/assessments"                    element={<AssessmentListPage />} />
                  <Route path="/assessments/:id"                element={<AssessmentDetailPage />} />
                  <Route path="/assessments/:id/checklist"      element={<ChecklistPage />} />
                  <Route path="/assessments/:sessionId/kpi"     element={<KpiFormPage />} />
                  <Route path="/checklists"                     element={<ChecklistsPage />} />
                  <Route path="/assessments/:id/remediations"   element={<RemediationPage />} />
                  
                  {/* Other Admin Routes */}
                  <Route path="/auto-audit"                     element={<AutoAuditPage />} />
                  <Route path="/security-audit"                 element={<SecurityAuditPage />} />
                  <Route path="/reports"                        element={<ReportsPage />} />
                  <Route path="/remediations"                   element={<GlobalRemediationsPage />} />
                  <Route path="/alerts"                         element={<AlertsPage />} />
                  <Route path="/users"                          element={<UsersPage />} />
                  <Route path="/settings"                       element={<SettingsPage />} />
                  {/* Forensic Store — ADMIN ONLY, không kết nối NDOP */}
                  <Route path="/forensic"                       element={<ForensicStorePage />} />

                  <Route path="/"                               element={<Navigate to="/dashboard" replace />} />
                </>
              ) : (
                <>
                  {/* User Portal Routes */}
                  <Route path="/portal/history"                 element={<PortalHistoryPage />} />
                  <Route path="/portal/report/new"              element={<PortalReportingForm />} />
                  <Route path="/portal/report/:id"              element={<PortalReportingForm />} />
                  <Route path="/portal/guidance"                element={<PortalGuidancePage />} />
                  
                  <Route path="/"                               element={<Navigate to="/portal/history" replace />} />
                </>
              )}
            </Routes>
          </MainLayout>
          </RequireAuth>
        }
      />
    </Routes>
  );
}
